import type { IssueTypeId } from '@atlassian/jira-shared-types/src/general.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { StoreActionApi } from '@atlassian/react-sweet-state';
import { logSafeErrorWithoutCustomerDataWrapper } from '@atlassian/jira-polaris-lib-errors/src/common/utils/index.tsx';
import type { FieldKey } from '@atlassian/jira-polaris-domain-field/src/field/types.tsx';
import { experience } from '@atlassian/jira-polaris-lib-analytics/src/common/constants/experience/index.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/index.tsx';
import { isNotFoundError } from '@atlassian/jira-polaris-lib-errors/src/controllers/utils.tsx';
import type { State } from '../types.tsx';
import { performUpdatePinnedFields } from '../../services/jira/update-pinned-fields.tsx';

export const updatePinnedFields =
	({ issueTypeId, pinnedFields }: { issueTypeId: IssueTypeId; pinnedFields: FieldKey[] }) =>
	async ({ setState, getState }: StoreActionApi<State>) => {
		experience.issueTypes.issueTypePinnedFieldsUpdate.start();

		const state = getState();

		try {
			const issueType = state.issueTypesById[issueTypeId];
			const projectId = Object.keys(state.issueTypesByProjectId).find((id) =>
				state.issueTypesByProjectId[id]?.includes(issueTypeId),
			);

			if (!issueType) {
				throw new Error(`Issue type with ID ${issueTypeId} not found`);
			}

			if (!projectId) {
				throw new Error(`Project ID for issue type ${issueTypeId} not found`);
			}

			setState({
				issueTypesById: {
					...state.issueTypesById,
					[issueTypeId]: {
						...issueType,
						pinnedFields,
					},
				},
			});

			await performUpdatePinnedFields({
				projectId,
				issueTypeId,
				pinnedFields,
			});
		} catch (maybeError) {
			// revert optimistic update
			setState(state);

			const error =
				maybeError instanceof Error ? maybeError : new Error('Error while updating pinned fields');

			if (isClientFetchError(error) || isNotFoundError(error)) {
				experience.issueTypes.issueTypePinnedFieldsUpdate.abort();
			} else {
				experience.issueTypes.issueTypePinnedFieldsUpdate.failure(error);
			}

			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.error.controllers.issue-types.actions.update-pinned-field',
				'Failed to update pinned fields',
				error,
			);

			throw error;
		}
	};

export const togglePinnedField =
	({ issueTypeId, fieldKey }: { issueTypeId: IssueTypeId; fieldKey: FieldKey }) =>
	async ({ getState, dispatch }: StoreActionApi<State>) => {
		const state = getState();
		const pinnedFields = state.issueTypesById[issueTypeId]?.pinnedFields ?? [];

		const isPinned = pinnedFields.includes(fieldKey);
		const updatedPinnedFields = isPinned
			? pinnedFields.filter((key) => key !== fieldKey)
			: [...pinnedFields, fieldKey];

		await dispatch(
			updatePinnedFields({
				issueTypeId,
				pinnedFields: updatedPinnedFields,
			}),
		);
	};
